import FoodMapDesktop from "@components/Modules/FoodMap/FoodMapElements/FoodMapDesktop";
import FoodMapGoogleMaps from "@components/Modules/FoodMap/FoodMapGoogleMaps";
import FoodMapLocationPermission from "@components/Modules/FoodMap/FoodMapLocationPermission";
import { getMetaData } from "@helper/getMetaData";
import { useBHCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall";
import { useVirtualPVAnalyticsCall } from "@hooks/useAnalyticsCall/useVirtualPVAnalyticsCall";
import useHidePrestitialOverlay from "@hooks/useHidePrestitialOverlay";
import useUserLocation from "@hooks/useUserLocation/useUserLocation";
import useWatchPosition from "@hooks/useUserLocation/useWatchPosition";
import ErrorPage from "@routes/ErrorPage";
import NotFound from "@routes/NotFound";
import { ResponseType, TRouteWithoutRedirect } from "@sphtech/web2-core/ssr";
import { useLoaderData } from "react-router";

import { type apiKeyParams, fetchGoogleMapId } from "./FoodMap.server";
import FoodMapMetaData from "./FoodMapMetaData";
import styles from "./FoodMapPage.module.css";
import useLocationValidation from "./hooks/useLocationValidation";
import useWatchLocationValidation from "./hooks/useWatchLocationValidation";

export function meta() {
  return [getMetaData(FoodMapMetaData())];
}

export default function FoodMapPage() {
  useHidePrestitialOverlay();
  const bhCustomEvent = useBHCustomEventAnalyticsCall();
  const virtualPVEvent = useVirtualPVAnalyticsCall();
  const { location, error, requestLocation, loading } = useUserLocation();
  const {
    userLocation,
    watchLocation,
    geoError,
    startWatching,
    setUserLocation,
    setWatchLocation,
    setGeoError,
  } = useLocationValidation({
    location,
    bhCustomEvent,
    virtualPVEvent,
    error,
  });
  const { userPosition, watchError } = useWatchPosition(startWatching);
  useWatchLocationValidation({
    userPosition,
    watchLocation,
    virtualPVEvent,
    watchError,
    setUserLocation,
    setWatchLocation,
    setGeoError,
  });

  const dataLoaderResponse: TRouteWithoutRedirect<apiKeyParams, string> =
    useLoaderData();
  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }
  const apiKey = dataLoaderResponse.payload.apiKey;

  return (
    <>
      <div
        className={`block fixed lg:static top-0 w-full h-full ${styles.foodmap}`}
      >
        <FoodMapDesktop />
        <div className={`flex flex-col lg:hidden max-w-[820px]`}>
          {userLocation !== undefined ? (
            <FoodMapGoogleMaps
              foodMapWrapper="flex"
              userLocation={userLocation}
              apiKey={apiKey}
            />
          ) : (
            <>
              {location == null && (
                <div className="flex flex-col items-center gap-3 p-3">
                  <p className="text-center text-md text-text-body">
                    We need to access your location to recommend halal food
                    eateries near you.
                  </p>
                  <button
                    onClick={requestLocation}
                    disabled={loading}
                    className={`${loading ? "bg-blue-100" : "bg-brand-primary"} text-text-inverse p-[6px] border-[1px] border-neutral-0 rounded-[4px]`}
                  >
                    {loading ? "Getting Location..." : "Allow Location Access"}
                  </button>
                </div>
              )}
              {geoError && <FoodMapLocationPermission error={geoError} />}
            </>
          )}
        </div>
      </div>
    </>
  );
}

export async function loader() {
  return await fetchGoogleMapId();
}
